import { useEffect, useState } from 'react';
import { Platform, Modal, View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';

const STORAGE_KEY = 'arafit_retention_tour_done_v2'; // bump quando o conteúdo mudar pra reexibir

interface Step {
  title: string;
  body: string;
}

const STEPS: Step[] = [
  {
    title: 'Boas-vindas à Área de Retenção',
    body: 'É aqui que você protege seu faturamento. Quando alguém da turma começa a sumir ou cair de frequência, aparece nesta área antes de virar problema. Bora dar uma volta de 1 minuto?',
  },
  {
    title: 'Pulso mensal',
    body: 'Quatro indicadores resumem como seu negócio está respirando: quantos estão ativos, qual a taxa de engajamento, se você cresceu no mês e quantos voltaram após sumir. Se algum número incomoda, você já sabe onde olhar.',
  },
  {
    title: 'Termômetro da turma',
    body: 'Cada aluno entra em uma de cinco zonas — do verde (engajado consistente) ao vermelho (abandono). A barra mostra a proporção da sua base agora. Quanto mais peso no laranja e vermelho, mais ações de retomada na semana.',
  },
  {
    title: 'Pra onde a coisa vai',
    body: 'O raio-X temporal das últimas 8 semanas. Em barras, você vê o tamanho de cada zona; em linhas, o movimento. Útil pra cruzar com o que mudou no seu trabalho: férias, mudança de plano, novo método.',
  },
  {
    title: 'Quem precisa de você agora',
    body: 'Use os filtros pra focar numa zona — em risco e abandono costumam ser prioridade. A busca acha por nome, email ou telefone. Sem rolagem infinita: já abre quem importa.',
  },
  {
    title: 'Mensagem certa, na hora certa',
    body: 'Escolha um grupo, pegue um modelo de mensagem que combina com o momento do aluno e ajuste o texto. Cada disparo abre o WhatsApp dele individualmente — você fala como sempre fala, sem cara de robô.',
  },
  {
    title: 'A ficha de cada um',
    body: 'Toque em qualquer aluno pra abrir o cartão completo: status, sparkline das últimas semanas e botão direto pra puxar conversa no WhatsApp. Decisão e ação no mesmo lugar.',
  },
];

// Storage cross-platform (web localStorage, mobile AsyncStorage)
const storage = {
  get: async (): Promise<string | null> => {
    if (Platform.OS === 'web') return Promise.resolve(localStorage.getItem(STORAGE_KEY));
    return AsyncStorage.getItem(STORAGE_KEY);
  },
  set: async (v: string): Promise<void> => {
    if (Platform.OS === 'web') { localStorage.setItem(STORAGE_KEY, v); return; }
    await AsyncStorage.setItem(STORAGE_KEY, v);
  },
};

/**
 * Tour exibido na primeira visita à Área de Retenção. Persiste a conclusão
 * no AsyncStorage/localStorage. Pode ser reaberto via prop `forceOpen`.
 */
export function RetentionTour({ forceOpen, onFinish }: { forceOpen?: boolean; onFinish?: () => void }) {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const insets = useSafeAreaInsets();

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (forceOpen) { if (!cancelled) { setStep(0); setVisible(true); } return; }
      const done = await storage.get();
      if (!cancelled && !done) { setStep(0); setVisible(true); }
    }
    check();
    return () => { cancelled = true; };
  }, [forceOpen]);

  function close(markDone = true) {
    setVisible(false);
    if (markDone) storage.set('1');
    onFinish?.();
  }

  function next() {
    if (step < STEPS.length - 1) setStep(step + 1);
    else close(true);
  }

  function prev() {
    if (step > 0) setStep(step - 1);
  }

  const s = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <Modal visible={visible} animationType="fade" transparent onRequestClose={() => close(true)}>
      <View style={styles.overlay}>
        <View style={[styles.card, { paddingBottom: 28 + insets.bottom }]}>
          <View style={styles.headerRow}>
            <Text style={styles.stepIndicator}>
              <Text style={styles.stepCurrent}>{step + 1}</Text>
              <Text style={styles.stepTotal}> / {STEPS.length}</Text>
            </Text>
            <TouchableOpacity onPress={() => close(true)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="close" size={22} color="#888" />
            </TouchableOpacity>
          </View>

          <Text style={styles.title}>{s.title}</Text>
          <Text style={styles.body}>{s.body}</Text>

          <View style={styles.footerRow}>
            <TouchableOpacity onPress={() => close(true)} style={styles.skipBtn}>
              <Text style={styles.skipText}>Pular tour</Text>
            </TouchableOpacity>

            <View style={styles.navRow}>
              <TouchableOpacity
                style={[styles.navBtn, step === 0 && styles.navBtnDisabled]}
                onPress={prev}
                disabled={step === 0}
              >
                <Text style={[styles.navBtnText, step === 0 && styles.navBtnTextDisabled]}>Anterior</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.nextBtn} onPress={next}>
                <Text style={styles.nextBtnText}>{isLast ? 'Concluir' : 'Próximo'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'flex-end', padding: 0,
  },
  card: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20, borderTopRightRadius: 20,
    padding: 22, paddingBottom: 28,
    maxWidth: 600, alignSelf: 'center', width: '100%',
  },
  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  stepIndicator: { flexDirection: 'row' },
  stepCurrent: { color: '#C8102E', fontSize: 18, fontWeight: '700' },
  stepTotal: { color: '#999', fontSize: 15 },

  title: { fontSize: 20, fontWeight: '800', color: '#1A1A1A', marginBottom: 10 },
  body: { fontSize: 14, color: '#444', lineHeight: 21, marginBottom: 24 },

  footerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  skipBtn: { padding: 8 },
  skipText: { color: '#C8102E', fontSize: 13, fontWeight: '600' },

  navRow: { flexDirection: 'row', gap: 8 },
  navBtn: { paddingVertical: 9, paddingHorizontal: 14, borderRadius: 8, borderWidth: 1, borderColor: '#C8102E', backgroundColor: '#fff' },
  navBtnDisabled: { borderColor: '#DDD' },
  navBtnText: { color: '#C8102E', fontWeight: '700', fontSize: 13 },
  navBtnTextDisabled: { color: '#BBB' },
  nextBtn: { paddingVertical: 9, paddingHorizontal: 14, borderRadius: 8, backgroundColor: '#C8102E' },
  nextBtnText: { color: '#fff', fontWeight: '700', fontSize: 13 },
});
